import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { COVER_ASPECT, getBlogSettings, getTag, listArticles, listBlogCategories, monthLabel, thumbOf } from "../lib/blogApi";
import type { BlogCard, BlogSettings } from "../lib/blogApi";
import { assetUrl } from "../lib/api";
import { cn, longDate } from "../lib/format";
import { ORGANIZATION_ID, absoluteUrl, breadcrumbLd, useSeo } from "../lib/seo";
import { useLang, useT } from "../i18n";

/** Cover in the 1200 × 630 shape; `thumb` loads the 600 × 315 version (grid cards). */
function Cover({ a, className, thumb = false }: { a: BlogCard; className?: string; thumb?: boolean }) {
  return (
    <div className={cn("flex items-center justify-center overflow-hidden rounded-lg bg-[#E4E4E4]", className)}>
      {a.coverImage ? (
        <img src={assetUrl(thumb ? thumbOf(a.coverImage) : a.coverImage)} alt={a.coverAlt || a.title} loading="lazy" className="h-full w-full object-cover" />
      ) : (
        <span className="font-mono text-xs text-faint">Le Coin VTC</span>
      )}
    </div>
  );
}

function Meta({ a }: { a: BlogCard }) {
  const t = useT();
  return (
    <span className="text-[11px] text-faint">
      {longDate(a.publishedAt.slice(0, 10))} · {t.blog.minutes(a.readingMinutes)}
    </span>
  );
}

/** /blog and /blog/tag/:tagSlug: latest article large, then a grid; category chips, search and pages. */
export default function Blog() {
  const t = useT();
  const b = t.blog;
  const lang = useLang(); // the API answers in the site language (X-Language)
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { tagSlug = "" } = useParams();
  const category = params.get("category") ?? "";
  const [query, setQuery] = useState(params.get("q") ?? "");
  const [categories, setCategories] = useState<{ name: string; slug: string }[]>([]);
  // Results tagged with their filters: changing them shows "loading" until the new list arrives
  const [result, setResult] = useState<{ key: string; items: BlogCard[]; total: number; error: boolean } | null>(null);
  // Title, intro and page size from Admin → Blog → Settings (the built-in texts until they arrive)
  const [settings, setSettings] = useState<BlogSettings | null>(null);
  // Display name of /blog/tag/:tagSlug, tagged with its slug (another tag shows its slug until loaded)
  const [tagInfo, setTagInfo] = useState<{ slug: string; name: string } | null>(null);
  const tagName = tagSlug ? (tagInfo?.slug === tagSlug ? tagInfo.name : tagSlug) : "";
  const title = settings?.title || b.title;
  const intro = settings?.intro || b.intro;
  // Tag pages have their own canonical; filters and search (?category, ?q…) point to /blog
  useSeo({
    // "Paris — Blog | Le Coin VTC"; the blog itself uses its own title ("Le Coin VTC Blog")
    title: tagName ? `${tagName} — ${b.band}` : title,
    fullTitle: !tagName,
    description: intro || b.metaDescription,
    path: tagSlug ? `/blog/tag/${tagSlug}` : "/blog",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: title,
        description: intro || b.metaDescription,
        url: absoluteUrl("/blog"),
        publisher: { "@id": ORGANIZATION_ID },
      },
      breadcrumbLd(tagName ? [[b.home, "/"], [b.band, "/blog"], [b.tag(tagName)]] : [[b.home, "/"], [b.band]]),
    ],
  });

  useEffect(() => {
    listBlogCategories().then(setCategories, () => setCategories([]));
    getBlogSettings().then(setSettings, () => setSettings(null));
  }, [lang]);

  useEffect(() => {
    if (tagSlug) getTag(tagSlug).then((tg) => setTagInfo({ slug: tagSlug, name: tg?.name ?? tagSlug }));
  }, [tagSlug]);

  // Search waits for a short pause in typing
  useEffect(() => {
    const id = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (query.trim()) next.set("q", query.trim());
      else next.delete("q");
      if ((next.get("q") ?? "") !== (params.get("q") ?? "")) next.delete("page");
      if (next.toString() !== params.toString()) setParams(next, { replace: true });
    }, 300);
    return () => clearTimeout(id);
  }, [query, params, setParams]);

  const q = params.get("q") ?? "";
  // From the article sidebar: /blog/tag/paris (or /blog?tag=…) and /blog?month=2026-10
  const tag = tagSlug || params.get("tag") || "";
  const month = /^\d{4}-\d{2}$/.test(params.get("month") ?? "") ? params.get("month")! : "";
  const page = Math.max(1, Number(params.get("page")) || 1);
  const perPage = settings?.perPage ?? 12;
  const key = `${category}|${q}|${tag}|${month}|${page}|${perPage}|${lang}`;
  useEffect(() => {
    let cancelled = false;
    listArticles({ category: category || undefined, q: q || undefined, tag: tag || undefined, month: month || undefined, page, limit: perPage }).then(
      (r) => !cancelled && setResult({ key, items: r.items, total: r.total, error: false }),
      () => !cancelled && setResult({ key, items: [], total: 0, error: true }),
    );
    return () => {
      cancelled = true;
    };
  }, [category, q, tag, month, page, perPage, key]);
  const items = result?.key === key ? result.items : null;
  const error = result?.key === key && result.error;
  const pages = result?.key === key ? Math.max(1, Math.ceil(result.total / perPage)) : 1;
  const goPage = (n: number) => {
    const next = new URLSearchParams(params);
    if (n > 1) next.set("page", String(n));
    else next.delete("page");
    setParams(next);
    window.scrollTo({ top: 0 });
  };

  const pick = (slug: string) => {
    const next = new URLSearchParams(params);
    if (slug) next.set("category", slug);
    else next.delete("category");
    next.delete("page");
    setParams(next, { replace: true });
  };

  // The large card only on the first page
  const list = items ?? [];
  const featured = page === 1 ? list[0] : undefined;
  const rest = page === 1 ? list.slice(1) : list;
  const chip = (active: boolean) =>
    cn("h-7 cursor-pointer whitespace-nowrap rounded border-0 px-2.5 text-xs font-medium", active ? "bg-ink text-white" : "bg-surface text-ink hover:bg-surface-hover");

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header activeHref="/blog" />
      <main className="mx-auto w-full max-w-300 flex-1 px-4 pt-10 pb-20 sm:px-6">
        <h1 className="m-0 text-3xl font-medium tracking-[-0.01em] sm:text-[34px]">{title}</h1>
        <p className="mt-2 mb-6 text-sm text-muted">{intro}</p>

        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-1.5 overflow-x-auto scrollbar-none">
            <button type="button" className={chip(!category)} onClick={() => pick("")}>
              {b.all}
            </button>
            {categories.map((c) => (
              <button key={c.slug} type="button" className={chip(category === c.slug)} onClick={() => pick(c.slug)}>
                {c.name}
              </button>
            ))}
          </div>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={b.search}
            aria-label={b.search}
            className="h-8 w-full rounded border-0 bg-surface px-3 text-xs text-ink outline-none focus:ring-1 focus:ring-brand sm:w-56"
          />
        </div>

        {(tag || month) && (
          <div className="mb-5 flex items-center gap-2 text-xs">
            <span className="rounded bg-ink px-2.5 py-1.5 font-medium text-white">{tag ? b.tag(tagName || tag) : monthLabel(month, t.format.locale)}</span>
            <button
              type="button"
              className="cursor-pointer border-0 bg-transparent p-0 text-muted underline hover:text-ink"
              onClick={() => {
                const next = new URLSearchParams(params);
                next.delete("tag");
                next.delete("month");
                next.delete("page");
                if (tagSlug) navigate(`/blog${next.toString() ? `?${next}` : ""}`);
                else setParams(next, { replace: true });
              }}
            >
              {b.clearFilter}
            </button>
          </div>
        )}

        {items === null ? (
          <div className="h-72 animate-pulse rounded-lg bg-surface" aria-busy="true" />
        ) : items.length === 0 ? (
          <p className="rounded-lg bg-surface px-6 py-12 text-center text-sm text-muted">{error ? b.error : b.empty}</p>
        ) : (
          <>
            {featured && (
              <Link to={`/blog/${featured.slug}`} className="group mb-8 grid items-center gap-5 text-ink no-underline md:grid-cols-2">
                <Cover a={featured} className={COVER_ASPECT} />
                <div>
                  {featured.category && <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-brand">{featured.category.name}</div>}
                  <h2 className="m-0 text-2xl font-medium leading-tight group-hover:underline">{featured.title}</h2>
                  <p className="mt-2 mb-2 text-[13px] leading-relaxed text-muted">{featured.excerpt}</p>
                  <Meta a={featured} />
                </div>
              </Link>
            )}
            <div className="grid gap-x-4 gap-y-7 sm:grid-cols-2 md:grid-cols-3">
              {rest.map((a) => (
                <Link key={a.slug} to={`/blog/${a.slug}`} className="group text-ink no-underline">
                  <Cover a={a} thumb className={`mb-2.5 ${COVER_ASPECT}`} />
                  {a.category && <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-brand">{a.category.name}</div>}
                  <h3 className="m-0 text-[15px] font-medium leading-snug group-hover:underline">{a.title}</h3>
                  <p className="mt-1 mb-1.5 line-clamp-3 text-xs leading-relaxed text-muted">{a.excerpt}</p>
                  <Meta a={a} />
                </Link>
              ))}
            </div>
            {pages > 1 && (
              <nav aria-label={b.pagination} className="mt-10 flex items-center justify-center gap-2 text-sm">
                <button type="button" disabled={page <= 1} onClick={() => goPage(page - 1)} className="h-9 cursor-pointer rounded border-0 bg-surface px-4 text-ink hover:bg-surface-hover disabled:cursor-default disabled:opacity-40">
                  {b.previous}
                </button>
                <span className="px-2 text-muted">{b.pageOf(page, pages)}</span>
                <button type="button" disabled={page >= pages} onClick={() => goPage(page + 1)} className="h-9 cursor-pointer rounded border-0 bg-surface px-4 text-ink hover:bg-surface-hover disabled:cursor-default disabled:opacity-40">
                  {b.next}
                </button>
              </nav>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
